import type { TServiceWiseItinerary } from "../ServiceWiseItineraries"
import type { TMedia } from "../Media"
import type { IUser } from "./../Auth"
import type { TTripDestination } from "../TripDestinations"
import {
	XHRInstance,
	IListResponse,
	ICursorListResponse,
} from "@sembark-travel/xhr"

export type TTransportServiceLocationPoint = {
	id: number
	name: string
	short_name: string
	location_id?: number
}

export type TTransportServiceLocation = {
	id: number
	short_code?: string
	name: string
	short_name: string
	from: TTransportServiceLocationPoint
	to?: TTransportServiceLocationPoint
	services: Array<ITransportService>
	media_url?: string | null
	thumb_image_url?: string | null
	latest_media?: TMedia
	trip_destinations?: Array<TTripDestination>
	is_disabled?: boolean
}

export interface ITransportService {
	id: number
	name: string
	short_name: string
	locations: TTransportServiceLocation
	service: string
	service_short_name?: string
	service_display_name: string
	location_id: number
	from_to: string
	from_to_short: string
	distance?: number
	/** Default Start time of the service */
	start_time?: string
	start_time_formatted?: string
	duration?: number
	end_time?: string
	end_time_formatted?: string
	duration_string: string
	duration_string_short: string
	comments?: string
	service_wise_itineraries?: Array<TServiceWiseItinerary>
	service_wise_itinerary?: TServiceWiseItinerary
	media_url?: string | null
	thumb_image_url?: string | null
	latest_media?: TMedia
	created_at: string
	created_by?: IUser
	deleted_at?: null
	deleted_by?: IUser
	closed_on_days_of_week?: Array<
		| "Sunday"
		| "Monday"
		| "Tuesday"
		| "Wednesday"
		| "Thursday"
		| "Friday"
		| "Saturday"
	>
	closed_on_date_intervals?: Array<{
		id: string
		name: string
		start_date: string
		end_date: string
	}>
	closed_on_days_or_dates?: Array<string>
	entry_type?: "manually" | "by_sheet" | "by_preload"
	pick_up_locations?: Array<string | TTransportServiceLocationPoint>
	drop_locations?: Array<string | TTransportServiceLocationPoint>
	set_hotel_as_default_pickup?: string | null
	set_hotel_as_default_drop?: string | null
	rate_expiry_date: null | string
	rate_uploaded_by?: IUser
	rate_expiry_date_local: string | null
	updated_at?: string
	updated_by?: IUser
}
export interface INormalizedService {
	id: number
	name: string
	short_name: string | undefined
	distance: number | undefined
	start_time_formatted?: string | undefined
	duration_string?: string | undefined
	end_time_formatted?: string | undefined
	closed_on_days_or_dates?: string[]
	created_by: IUser | undefined
	created_at: string | undefined
	updated_by: IUser | undefined
	updated_at: string | undefined
	rate_uploaded_by: IUser | undefined
	rate_expiry_date: string | undefined
	rate_expiry_date_local?: string | undefined
	deleted_at: string | undefined | undefined
	deleted_by: IUser | undefined
	latest_media: TMedia | undefined
	description: string | undefined
}

export function transportServicesXHR(xhr: XHRInstance) {
	return {
		async getTransportServices(
			params?: unknown
		): Promise<IListResponse<ITransportService>> {
			return xhr
				.get("/transport-services", { params })
				.then((resp) => resp.data)
		},
		async getTransportServiceLocations(
			params?: unknown
		): Promise<ICursorListResponse<TTransportServiceLocation>> {
			return xhr
				.get("/transport-service-locations", { params })
				.then((resp) => resp.data)
		},
		async getTransportServiceLocationPoints(
			params?: unknown
		): Promise<IListResponse<TTransportServiceLocationPoint>> {
			return xhr
				.get("/transport-service-location-points", { params })
				.then((resp) => resp.data)
		},
		async getTransportServiceLocationsWithPaginate(
			params?: unknown
		): Promise<IListResponse<TTransportServiceLocation>> {
			return xhr
				.get("/transport-service-locations", { params })
				.then((resp) => resp.data)
		},
	}
}

export const checkFilterAppliedFn = <T>(
	params: T,
	...excludeKeys: (keyof T)[]
): boolean => {
	for (const key in params) {
		if (excludeKeys.includes(key as keyof T)) continue
		const v = params[key]
		if (Array.isArray(v)) {
			if (v.length > 0) return true
			continue
		}

		if (v !== "#" && !!v && v !== "0") {
			return true
		}
	}

	return false
}

export interface NewServiceCredentials {
	from?: TTransportServiceLocationPoint
	to?: TTransportServiceLocationPoint
	quick_add?: boolean
	short_code?: string
	transport_service?: Array<{
		service: string
		service_short_name: string
		distance?: number
		start_time?: Date
		duration?: number
		service_title?: string
		service_details?: string
		closed_on_days_of_week?: Array<string>
		closed_on_date_intervals?: Array<{ start_date?: Date; end_date?: Date }>
		pick_up_locations?: Array<
			string | undefined | TTransportServiceLocationPoint
		>
		drop_locations?: Array<string | undefined | TTransportServiceLocationPoint>
		set_hotel_as_default_pickup?: string | null
		set_hotel_as_default_drop?: string | null
	}>
	trip_destinations?: Array<TTripDestination>
	use_same_locations?: boolean
	pick_up_locations?: Array<string | undefined | TTransportServiceLocationPoint>
	drop_locations?: Array<string | undefined | TTransportServiceLocationPoint>
	set_hotel_as_default_pickup?: string | null
	set_hotel_as_default_drop?: string | null
}
